import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { IMAGES } from "@/data/images";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", lead_source: "" });
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => setSources(data.lead_sources)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      loginWithToken(data.token, data.user);
      toast.success("Account created");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center px-6 py-12 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t("auth.backHome")}
          </Link>
          <h1 className="font-serif font-light text-4xl text-white mb-2">{t("auth.createAccount")}</h1>
          <p className="text-muted-foreground text-sm mb-8">{t("auth.createSub")}</p>

          <Button data-testid="google-register-btn" variant="outline"
            className="w-full h-12 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 mb-6"
            onClick={googleLogin}>
            <svg className="h-4 w-4 mr-3" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41.4 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            {t("auth.google")}
          </Button>

          <div className="flex items-center gap-4 mb-6">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-xs text-muted-foreground uppercase tracking-luxe">{t("auth.or")}</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.fullName")}</Label>
              <Input data-testid="register-name" required value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="John Doe" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.email")}</Label>
              <Input data-testid="register-email" type="email" required value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-luxe text-muted-foreground">Phone</Label>
              <Input data-testid="register-phone" value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="+1 (514) 555-0199" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("auth.password")}</Label>
              <Input data-testid="register-password" type="password" required value={form.password}
                onChange={(e) => set("password", e.target.value)}
                className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="••••••••" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.leadSource")}</Label>
              <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
                <SelectTrigger data-testid="register-lead-source" className="mt-2 h-12 bg-secondary border-white/10 text-white">
                  <SelectValue placeholder={t("booking.selectSource")} />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="register-submit" type="submit" disabled={loading}
              className="w-full h-12 rounded-full bg-white text-black hover:bg-white/85">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.createBtn")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-8 text-center">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" data-testid="to-login-link" className="text-white hover:underline">{t("auth.signinLink")}</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={IMAGES.interior} alt="Luxury interior" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
      </div>
    </div>
  );
}

